import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
  Slider as MuiSlider,
  type SliderProps as MuiSliderProps,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  useTheme
} from '@mui/material';
import { mergeSx } from 'merge-sx';

import withFormControl from '@/hoc/withFormControl';
import { useDirection } from '@/hooks/useDirection';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { transforms } from '@/theme/constants';
import shadows from '@/theme/shadows';
import typography from '@/theme/typography';

import DropdownSelect from './DropdownSelect';

export interface Scale {
  value: number;
  label?: string;
  getLabel?: (count: number) => string;
  min?: number;
  max?: number;
}

interface SliderProps extends Omit<MuiSliderProps, 'onChange'> {
  id?: string;
  name?: string;
  value?: number;
  min?: number;
  max?: number;
  minFieldName?: string;
  maxFieldName?: string;
  onChange?: <Event>(event: Event) => void;
  sx?: SxProps<Theme>;
  scales: Scale[];
  forceMinValue?: boolean;
  disabled?: boolean;
}

const capValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getScaleForValue = (value: number, scales: { value: number }[]): number => {
  const sortedScales = [...scales].sort((a, b) => b.value - a.value);

  return sortedScales.find(scale => scale.value <= value)?.value || scales[0]?.value || 1;
};

const getAbsoluteBoundsForScale = (
  scales: Scale[],
  scale: number
): { minAbs: number | undefined; maxAbs: number | undefined } => {
  const scaleObj = scales.find(s => s.value === scale);
  if (!scaleObj) return { minAbs: undefined, maxAbs: undefined };

  return {
    minAbs: scaleObj.min !== undefined ? scaleObj.min * scale : undefined,
    maxAbs: scaleObj.max !== undefined ? scaleObj.max * scale : undefined
  };
};

const SliderField: FC<SliderProps> = ({
  id,
  value,
  min = 0,
  max = 365,
  minFieldName,
  maxFieldName,
  onChange,
  sx,
  scales = [],
  forceMinValue,
  disabled,
  ...props
}) => {
  const { spacing } = useTheme();
  const { isRtl, applyDirectionalStyles } = useDirection();
  const { getPrefixedFieldName } = useFieldPrefix();
  const [currentValue, setCurrentValue] = useState(value || min);

  const minWatched = useWatch({
    name: minFieldName ? getPrefixedFieldName(minFieldName) : ''
  });
  const maxWatched = useWatch({
    name: maxFieldName ? getPrefixedFieldName(maxFieldName) : ''
  });

  const initialScale = useMemo(() => getScaleForValue(value || min, scales), [value, min, scales]);
  const [selectedScale, setSelectedScale] = useState(initialScale);

  const { minAbs: scaleMinAbs, maxAbs: scaleMaxAbs } = getAbsoluteBoundsForScale(scales, selectedScale);
  const isMinWatched = minFieldName && typeof minWatched === 'number';
  const isMaxWatched = maxFieldName && typeof maxWatched === 'number';
  const minWatchedValue = isMinWatched ? minWatched : undefined;
  const maxWatchedValue = isMaxWatched ? maxWatched : undefined;

  const dynamicMinAbs = useMemo(
    () => Math.max(...[minWatchedValue, scaleMinAbs, min].filter((item): item is number => Number.isFinite(item!))),
    [min, minWatchedValue, scaleMinAbs]
  );
  const dynamicMaxAbs = useMemo(
    () => Math.min(...[maxWatchedValue, scaleMaxAbs, max].filter((item): item is number => Number.isFinite(item!))),
    [maxWatchedValue, scaleMaxAbs, max]
  );

  const scaledValue = Math.round((currentValue || 0) / selectedScale);
  const scaleOptions = useMemo(
    () =>
      (scales || [])
        .map(({ value, label, getLabel }) => ({
          value: (value ?? '').toString(),
          label: getLabel ? getLabel(scaledValue) : label!
        }))
        .filter(({ value: scaleValue }) => {
          const { minAbs, maxAbs } = getAbsoluteBoundsForScale(scales, Number(scaleValue));

          return (!minAbs || minAbs <= (maxWatchedValue ?? max)) && (!maxAbs || maxAbs >= (minWatchedValue ?? min));
        }),
    [scales, scaledValue, maxWatchedValue, max, minWatchedValue, min]
  );

  const formatValue = useCallback(
    (val: number) => {
      const count = Math.round(val / selectedScale);
      const scaleObj = scales.find(s => s.value === selectedScale);

      return `${count} ${scaleObj?.getLabel?.(count) ?? scaleObj?.label ?? ''}`.trim();
    },
    [selectedScale, scales]
  );

  const onChangeCurrentValue = useCallback(
    (_event: Event, value: number | number[]) => {
      const numericValue = value as number;
      const snappedToScale = Math.round(numericValue / selectedScale) * selectedScale;
      const cappedValue = capValue(snappedToScale, Number(dynamicMinAbs), Number(dynamicMaxAbs));

      setCurrentValue(cappedValue);
    },
    [selectedScale, dynamicMinAbs, dynamicMaxAbs]
  );

  const onChangeCommitted = useCallback(
    (_event: Event | React.SyntheticEvent, committedValue: number | number[]) => {
      const numericValue = committedValue as number;
      onChange?.(numericValue);
    },
    [onChange]
  );

  const onScaleChange = useCallback(
    (newScale: string | string[]) => {
      const newScaleNumericValue = parseInt(newScale?.toString());
      setSelectedScale(newScaleNumericValue);

      const { minAbs: newScaleMinAbs, maxAbs: newScaleMaxAbs } = getAbsoluteBoundsForScale(
        scales,
        newScaleNumericValue
      );

      const newDynamicMinAbs = minWatchedValue ?? newScaleMinAbs ?? min;
      const newDynamicMaxAbs = maxWatchedValue ?? newScaleMaxAbs ?? max;

      const snapped = Math.round((currentValue || 0) / newScaleNumericValue) * newScaleNumericValue;
      const newValue = capValue(snapped, Number(newDynamicMinAbs), Number(newDynamicMaxAbs));

      setCurrentValue(newValue);
      onChange?.(newValue);
    },
    [scales, minWatchedValue, min, maxWatchedValue, max, currentValue, onChange]
  );

  const onScaledValueChange = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newScaledValue = parseInt(changeEvent.target.value) || 0;
      const minScaledValue = Number(dynamicMinAbs) / selectedScale;
      const maxScaledValue = Number(dynamicMaxAbs) / selectedScale;

      const cappedScaledValue = capValue(newScaledValue, minScaledValue, maxScaledValue);
      const newValue = cappedScaledValue * selectedScale;

      setCurrentValue(newValue);
      onChange?.(newValue);
    },
    [dynamicMinAbs, selectedScale, dynamicMaxAbs, onChange]
  );

  useEffect(() => {
    if (value === currentValue) return;
    if (!forceMinValue && !Number.isFinite(value)) return;

    const calculatedValue = forceMinValue ? value ?? Number(dynamicMinAbs) : value!;
    const calculatedScale = getScaleForValue(calculatedValue, scales);
    const snappedToScale = Math.round(calculatedValue / calculatedScale) * calculatedScale;

    setCurrentValue(snappedToScale);
    setSelectedScale(calculatedScale);
  }, [value, forceMinValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentValue !== undefined && Number.isFinite(currentValue)) {
      const cappedValue = capValue(currentValue, Number(dynamicMinAbs), Number(dynamicMaxAbs));
      if (cappedValue !== currentValue) {
        const calculatedScale = getScaleForValue(cappedValue, scales);
        const scaledValue = Math.round(cappedValue / calculatedScale);

        setCurrentValue(scaledValue * calculatedScale);
        setSelectedScale(calculatedScale);
      }
    }
  }, [minWatchedValue, maxWatchedValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack direction="row" alignItems="center">
      <MuiSlider
        value={currentValue || Number(dynamicMinAbs)}
        // The onChange is for the slider internal value change synchronization
        onChange={onChangeCurrentValue}
        // The onChangeCommitted is for react-hook-form (reduces the need for debouncing and hence bugs related to updating the form value too late)
        onChangeCommitted={onChangeCommitted}
        min={Number(dynamicMinAbs)}
        max={Number(dynamicMaxAbs)}
        disabled={disabled}
        {...props}
        step={selectedScale}
        valueLabelFormat={formatValue}
        valueLabelDisplay={props.valueLabelDisplay ?? 'auto'}
        sx={mergeSx(
          {
            ...applyDirectionalStyles(0.5, 2, 'ml', 'mr'),
            height: spacing(1),
            filter: disabled ? 'grayscale(100%)' : 'none',
            '& .MuiSlider-rail, & .MuiSlider-track': {
              bgcolor: 'primary.200',
              border: 'none',
              opacity: 1
            },
            '& .MuiSlider-thumb': {
              bgcolor: 'primary.600',
              transform: transforms.compose(transforms.shift50X(!isRtl), transforms.shift50Y(true))
            },
            '& .MuiSlider-thumb::before': {
              boxShadow: shadows[5]
            },
            '& .MuiSlider-thumb::after': {
              bgcolor: 'primary.250',
              width: spacing(1.5),
              height: spacing(1.5)
            }
          },
          sx
        )}
      />
      <TextField
        id={id}
        sx={{
          mx: 0.5,
          pb: 0,
          minWidth: 40,
          '& .MuiInputBase-root': {
            width: 40
          },
          '& .MuiInputBase-root .MuiInputBase-input': {
            pt: 0.75,
            pb: 0.625,
            color: 'grey.700'
          },
          '& .MuiInputBase-input::-webkit-inner-spin-button, & .MuiInputBase-input::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
          }
        }}
        type="number"
        value={scaledValue}
        onChange={onScaledValueChange}
        inputProps={{ inputMode: 'numeric' }}
        disabled={disabled}
      />
      <DropdownSelect
        name="slider-select"
        options={scaleOptions}
        value={selectedScale?.toString() || ''}
        onChange={onScaleChange}
        isMulti={false}
        usePortal
        isDisabled={disabled}
        sx={{
          minWidth: 80,
          '& .DropdownSelect-control': { border: 'none', boxShadow: 'none', minHeight: spacing(3.5) },
          '& .DropdownSelect-indicatorsContainer > div': {
            px: 0
          },
          '& .DropdownSelect-singleValue .MuiTypography-root': {
            ...typography.body3,
            color: 'grey.700'
          },
          '& .DropdownSelect-expandMoreIcon': {
            color: 'grey.700'
          },
          '& .DropdownSelect-optionLabel .MuiTypography-root': {
            color: 'grey.700'
          }
        }}
      />
    </Stack>
  );
};

const Slider = withFormControl<Event, number, SliderProps>(SliderField);

export default Slider;
